import React from 'react';
import { AppBar, Box, Button, Link, Toolbar, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { LABOKLIN_RED, LABOKLIN_APPBAR_HEIGHT, LABOKLIN_BORDER } from '../../theme/laboklinTheme';

/**
 * Laboklin MyLab page chrome.
 *
 * Reproduces the portal's navbar, page heading and footer. The AppBar height is
 * pinned to LABOKLIN_APPBAR_HEIGHT because ChatInterface sizes itself against a
 * 64px top bar; changing one without the other leaves the composer misplaced.
 *
 * Nav entries other than MyLab are inert - they exist so the surrounding page
 * reads as the real portal, not because the PoC implements them.
 */

const INERT_NAV_ITEMS = ['Submission forms', 'Prices-Products Catalogue', 'Shipping materials', 'Book courier'];

interface LaboklinChromeProps {
  title: string;
  userLabel: string;
  onSignOut: () => void;
  children: React.ReactNode;
}

export const LaboklinChrome: React.FC<LaboklinChromeProps> = ({
  title,
  userLabel,
  onSignOut,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ height: LABOKLIN_APPBAR_HEIGHT, minHeight: LABOKLIN_APPBAR_HEIGHT }}>
          <Box
            onClick={() => navigate('/mylab')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 4 }}
          >
            <Typography
              sx={{ fontWeight: 800, letterSpacing: '-0.5px', color: LABOKLIN_RED, fontSize: 20 }}
            >
              LABOKL<Box component="span" sx={{ color: '#212529' }}>IN</Box>
            </Typography>
          </Box>

          <Button
            onClick={() => navigate('/mylab')}
            sx={{
              color: LABOKLIN_RED,
              fontWeight: 600,
              borderBottom: `2px solid ${LABOKLIN_RED}`,
              borderRadius: 0,
              mr: 1,
            }}
          >
            MyLab
          </Button>

          {INERT_NAV_ITEMS.map((label) => (
            <Button key={label} disableRipple sx={{ color: '#212529', mr: 0.5 }}>
              {label}
            </Button>
          ))}

          <Box sx={{ flexGrow: 1 }} />

          <Typography sx={{ fontWeight: 800, letterSpacing: '-0.5px', color: LABOKLIN_RED, fontSize: 22 }}>
            LABOKL<Box component="span" sx={{ color: '#212529' }}>IN</Box>
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Spacer for the fixed AppBar */}
      <Box sx={{ height: `${LABOKLIN_APPBAR_HEIGHT}px`, flexShrink: 0 }} />

      <Box sx={{ flexGrow: 1, px: { xs: 2, md: 4 }, py: 3, maxWidth: 1400, width: '100%', mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h2" component="h1">
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">{userLabel}</Typography>
            </Box>
            <Link
              component="button"
              onClick={onSignOut}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 15 }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              Sign out
            </Link>
          </Box>
        </Box>

        {children}
      </Box>

      <Box
        component="footer"
        sx={{
          py: 2,
          borderTop: `1px solid ${LABOKLIN_BORDER}`,
          display: 'flex',
          justifyContent: 'center',
          gap: 3,
          flexWrap: 'wrap',
          color: 'text.secondary',
          fontSize: 14,
        }}
      >
        {['LABOKLIN Website', 'Imprint', 'Privacy protection', 'Terms and conditions', 'Courier information'].map(
          (label) => (
            <Typography key={label} variant="body2" sx={{ color: 'text.secondary' }}>
              {label}
            </Typography>
          )
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          © Copyright {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );
};

export default LaboklinChrome;
